import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { SisifoCenter } from '@app/core/users';
import {
  CenterService,
  SisifoHospital,
} from '@app/features/patient-requests/services/center.service';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { ShoppingCartItem } from '@app/shared/select-shopping-cart/select-shopping-cart.component';

@Component({
  selector: 'afoone-center-dialog',
  templateUrl: './center-dialog.component.html',
  styleUrls: ['./center-dialog.component.css'],
})
export class CenterDialogComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public saving: boolean;

  private destroyed: boolean;
  public center: SisifoCenter;
  public hospitals: SisifoHospital[] = [];

  public availableServices: ShoppingCartItem[] = [];

  private testServices: ShoppingCartItem[] = [];

  constructor(
    cfg: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private service: CenterService
  ) {
    this.form = new FormGroup({
      centroId: new FormControl(''),
      centroName: new FormControl('', [Validators.required]),
      selectedHospital: new FormControl(null),
      assignedServices: new FormControl([]),
    });

    if (cfg.data?.center) {
      this.center = cfg.data.center;
      this.form.patchValue({
        ...this.center,
        assignedServices: this.testServices, // Assuming you want to set some default services
      });
      // disable centroId if center is being edited
      this.form.get('centroId')?.disable();
    }
  }

  ngOnInit(): void {
    this.service
      .getHospitals()
      .then((hospitals) => {
        this.hospitals = hospitals;
        this.availableServices = this.availableServiceItems(hospitals);

        // Cargar servicios asignados después de tener los hospitales
        if (this.center?.centroId) {
          this.service.getCenter(this.center?.centroId).then((center) => {
            if (center) {
              this.center = center;
              this.form.patchValue({
                ...this.center,
                assignedServices: this.assignedServiceItems(center, hospitals),
              });
            }
          });
        }
      })
      .catch((error) => {
        console.error('Error loading hospitals:', error);
      });
  }

  private serviceItem(service: { statKrz: string; statName?: string }, hospitalName: string): ShoppingCartItem {
    return {
      id: service.statKrz,
      label: service.statName || service.statKrz,
      value: service.statKrz,
      data: { hospitalName },
    };
  }

  private availableServiceItems(hospitals: SisifoHospital[]): ShoppingCartItem[] {
    return hospitals
      .flatMap((hospital) =>
        hospital.services?.map((service) => this.serviceItem(service, hospital.anstName)) || []
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private assignedServiceItems(center: SisifoCenter, hospitals: SisifoHospital[]): ShoppingCartItem[] {
    return center.services?.map((service) => {
      const hospital = hospitals.find((h) =>
        h.services?.some((s) => s.statKrz === service.statKrz)
      );
      return this.serviceItem(service, hospital?.anstName || '');
    }) || [];
  }

  public async onHospitalChange(event: DropdownChangeEvent): Promise<void> {
    const newHospital = event.value as SisifoHospital;
    if (!newHospital) {
      this.availableServices = this.availableServiceItems(this.hospitals);
      return;
    }

    const hospital = await this.service.getHospital(newHospital.anstaltNr);

    this.availableServices =
      hospital?.services
        ?.map((service) => this.serviceItem(service, hospital.anstName))
        .sort((a, b) => a.label.localeCompare(b.label)) || [];
  }

  public async saveChanges(): Promise<void> {
    this.form.markAllAsTouched();

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key).markAsDirty();
    });

    if (!this.form.valid) {
      return;
    }

    this.saving = true;

    const response = this.center
      ? await this.service.update({ ...this.center, ...this.form.value })
      : await this.service.create({ ...this.form.value });

    this.dialogRef.close(response);
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
  }
}
